import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Achievements from "../../component/profile/view/achievements";
import BasicDetails from "../../component/profile/view/basicDetails";
import FighterDetails from "../../component/profile/view/fighterDetails";
import FightHistory from "../../component/profile/view/fightHistory";
import Sponsorships from "../../component/profile/view/sponsorships";
import VideoUrl from "../../component/profile/view/videoUrl";
import YouTubeLinks from "../../component/profile/view/youtubeLinks";
import {
  createPublicProfileView,
  getAllRatings,
  getProfileById,
} from "../../services/api";
import { Profile, RatingResponseDto } from "../../types/types";
import Ratings from "../../component/profile/view/ratings";
import { PostSection } from "../../component/profile/view/postSection";

const PublicViewProfile: React.FC = () => {
  const [searchParams] = useSearchParams();
  const profileId = searchParams.get("profileId");
  const [member, setMember] = useState<Profile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("basic-details");
  const [ratingData, setRatingData] = useState<RatingResponseDto[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [isRatingsLoading, setIsRatingsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRatingElements, setTotalRatingElements] = useState(0);
  const isRatingsLoadingRef = useRef(false);

  const { t, i18n } = useTranslation("publicProfile");

  useEffect(() => {
    const savedLanguage = localStorage.getItem("i18nextLng") || "en";
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  useEffect(() => {
    const fetchMember = async () => {
      try {
        if (!profileId) return;

        setLoading(true);
        const res = await getProfileById(profileId);
        if (res.success && res.data) {
          setMember(res.data);
        } else {
          setError(t("profile_not_found"));
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || t("profile_not_found"));
      } finally {
        setLoading(false);
      }
    };

    const trackGeoLocation = async () => {
      try {
        const response = await fetch("https://ipapi.co/json");
        const data = await response.json();

        if (!profileId) return;

        const dto = {
          viewedProfileId: profileId,
          informationRequestDto: {
            ipAddress: data.ip,
            countryCode: data.country_code,
            city: data.city,
            browserInfo: navigator.userAgent,
            sourcePage: window.location.pathname,
          },
        };

        await createPublicProfileView(dto);
      } catch (error) {
        console.error("Geo tracking failed:", error);
      }
    };

    const handleTracking = () => {
      const key = `lastViewed_${profileId}`;
      const lastViewed = localStorage.getItem(key);

      if (!lastViewed || Date.now() - Number(lastViewed) > 300000) {
        trackGeoLocation();
        localStorage.setItem(key, Date.now().toString());
      } else {
        console.log("Skipping tracking: viewed recently");
      }
    };

    fetchMember();
    handleTracking();
  }, [profileId]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [activeTab]);

const loadRatingsAPI = async (page = 0) => {
  if(isRatingsLoadingRef.current || !profileId) return;
  isRatingsLoadingRef.current = true;
  setIsRatingsLoading(true);
  try {
    const res = await getAllRatings(profileId, page, 10);
    const content = res.content ?? [];
    setRatingData(content);

    setCurrentPage(res.number ?? page);
    setTotalPages(res.totalPages ?? 1);
    setTotalRatingElements(res.totalElements ?? 0);
  } catch(e){
    console.error(e);
  } finally { 
    isRatingsLoadingRef.current = false; 
    setIsRatingsLoading(false); 
  }
}

useEffect(() => {
  setRatingData([]);
  setCurrentPage(0);
  setTotalPages(1);
  setTotalRatingElements(0);
}, [profileId]);


  const RatingsLoader = () => (
    <div className="w-full py-4 flex justify-center items-center">
      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-500"></div>
    </div>
  );

const scrollToSection = (sectionId: string) => {
  const sectionElement = sectionRefs.current[sectionId];
  if (sectionElement && scrollContainerRef.current) {
    const sectionTop =
      sectionElement.offsetTop - scrollContainerRef.current.offsetTop;
    scrollContainerRef.current.scrollTo({
      top: sectionTop,
      behavior: "smooth",
    });
  }

  // ✅ Load first page of ratings if Ratings tab
  if (sectionId === "ratings" && ratingData.length === 0) {
    loadRatingsAPI(0);
  }

  setActiveTab(sectionId);
};

const handleScroll = () => {
  if (!scrollContainerRef.current) return;
  const container = scrollContainerRef.current;

  // 1️⃣ Update active tab
  const containerTop = container.scrollTop;
  const containerHeight = container.clientHeight;

  let mostVisibleSection = tabs[0].id;
  let maxVisibleHeight = 0;

  tabs.forEach((tab) => {
    const sectionElement = sectionRefs.current[tab.id];
    if (sectionElement) {
      const sectionTop = sectionElement.offsetTop - container.offsetTop;
      const sectionBottom = sectionTop + sectionElement.offsetHeight;
      const visibleTop = Math.max(containerTop, sectionTop);
      const visibleBottom = Math.min(containerTop + containerHeight, sectionBottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      if (visibleHeight > maxVisibleHeight) {
        maxVisibleHeight = visibleHeight;
        mostVisibleSection = tab.id;
      }
    }
  });

  if (mostVisibleSection !== activeTab) {
    setActiveTab(mostVisibleSection);
  }

  // 2️⃣ Trigger ratings load if scrolling near bottom of Ratings section
};


  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">{t("loading")}</div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-red-600">
          {error || t("profile_not_found")}
        </div>
      </div>
    );
  }

  const tabs = [
    {
      id: "basic-details",
      title: t("basic_details").toUpperCase(),
      content: <BasicDetails member={member} />,
    }
  ];
    if (member.manager) {
            tabs.push(
                {
        id: "ratings",
        title: t("ratings").toUpperCase(),
        content: (
          <>
            <Ratings
              member={member}
              ratings={ratingData}
              currentPage={currentPage}
              totalPages={totalPages}
              totalElements={totalRatingElements}
              isLoading={isRatingsLoading}
              onPageChange={loadRatingsAPI}
            />
            {isRatingsLoading && <RatingsLoader />}
          </>
        ),
      }
            )
    }

  if (member.fighter) {
    tabs.push(
      {
        id: "fighter-details",
        title: t("fighter_details").toUpperCase(),
        content: <FighterDetails member={member} />,
      },
      {
        id: "fight-history",
        title: t("fight_history").toUpperCase(),
        content: <FightHistory member={member} />,
      },
      {
        id: "achievements",
        title: t("achievements").toUpperCase(),
        content: <Achievements member={member} />,
      },
      {
        id: "video-url",
        title: t("video_url").toUpperCase(),
        content: <VideoUrl member={member} />,
      },
      {
        id: "youtube-links",
        title: t("youtube_links").toUpperCase(),
        content: <YouTubeLinks member={member} />,
      },
      {
        id: "sponserships",
        title: t("sponserships").toUpperCase(),
        content: <Sponsorships member={member} />,
      },
      {
        id: "ratings",
        title: t("ratings").toUpperCase(),
        content: (
          <>
            <Ratings
              member={member}
              ratings={ratingData}
              currentPage={currentPage}
              totalPages={totalPages}
              totalElements={totalRatingElements}
              isLoading={isRatingsLoading}
              onPageChange={loadRatingsAPI}
            />
            {isRatingsLoading && <RatingsLoader />}
          </>
        ),
      }
    );
  }

  tabs.push({
    id: "posts",
    title: t("posts").toUpperCase(),
    content: <PostSection profileId={profileId ?? member.id} />,
  });

  return (
    <div className="mx-auto overflow-hidden h-[90vh] relative">
      <div className="grid grid-cols-12 gap-6">
        <div
          ref={scrollContainerRef}
          className="col-span-9 p-6 bg-content1 overflow-y-auto max-h-[90vh]"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <style
            dangerouslySetInnerHTML={{
              __html: `
                .col-span-9::-webkit-scrollbar {
                  display: none;
                }
              `,
            }}
          />
          {tabs.map((tab) => (
            <div
              key={tab.id}
              ref={(el) => {
                sectionRefs.current[tab.id] = el;
              }}
              id={tab.id}
            >
              {tab.content}
            </div>
          ))}
        </div>

        <div className="col-span-3 p-4 mt-4 flex flex-col h-full space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`w-full h-8 font-medium justify-start text-left px-3 rounded-md text-xs transition-all duration-150 ${
                activeTab === tab.id
                  ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20"
                  : "bg-gray-100 text-slate-700 hover:bg-gray-200"
              }`}
              onClick={() => scrollToSection(tab.id)}
            >
              {tab.title}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PublicViewProfile;
